import { appConfig } from "@/constants";
import type {
  GemRewardPrize,
  RewardEarning,
  SweepstakeFreeEntryPrize,
  SweepstakeTicketUpgradePrize,
  TokenRewardPrize,
} from "@/domain/gems-game/domain/lib/Earning";
import { getRarityTierLevel } from "@/utils/rewards";
import { ButtCon, Modal } from "@biom3/react";
import { useEffect, useMemo } from "react";

interface SweepStakePrizeModalProps {
  earning: RewardEarning;
  onClose?: () => void;
}

export const SweepStakePrizeModal = ({
  earning,
  onClose,
}: SweepStakePrizeModalProps) => {
  const url = useMemo(() => {
    const tierLevel = getRarityTierLevel(earning?.rarity);

    let primaryPrizeType = "";
    let primaryPrizeValue: number | string = 0;
    let secondaryPrizeType = "";
    let secondaryPrizeValue: number | string = 0;
    let jackpotValue: number | string = 0;

    if (earning?.metadata && Array.isArray(earning?.metadata)) {
      // Find all token prizes (IMX)
      const tokenPrizes = earning.metadata.filter(
        (prize): prize is TokenRewardPrize => prize.type === "token",
      );

      // Find gem prize
      const gemPrize = earning.metadata.find(
        (prize): prize is GemRewardPrize => prize.type === "point",
      );

      // Find free entry prize
      const freeEntryPrize = earning.metadata.find(
        (prize): prize is SweepstakeFreeEntryPrize =>
          prize.type === "sweepstake_free_entry",
      );

      // Find ticket upgrade prize
      const ticketUpgradePrize = earning.metadata.find(
        (prize): prize is SweepstakeTicketUpgradePrize =>
          prize.type === "sweepstake_ticket_upgrade",
      );

      // Set primary prize - token gets priority
      if (tokenPrizes.length > 0) {
        primaryPrizeType = "IMX";

        // Set jackpot value from jackpot tokens
        const jackpotTokens = tokenPrizes.filter(
          (token) =>
            token.metadata.reward_source === "jackpot" ||
            token.metadata.is_jackpot,
        );
        if (jackpotTokens.length > 0) {
          jackpotValue = jackpotTokens.reduce((sum, tokenPrize) => {
            return sum + Number.parseFloat(tokenPrize.metadata.amount);
          }, 0);
        }

        // Set primary value from non-jackpot tokens
        const nonJackpotTokens = tokenPrizes.filter(
          (token) =>
            !(
              token.metadata.reward_source === "jackpot" ||
              token.metadata.is_jackpot
            ),
        );
        if (nonJackpotTokens.length > 0) {
          primaryPrizeValue = nonJackpotTokens.reduce((sum, tokenPrize) => {
            return sum + Number.parseFloat(tokenPrize.metadata.amount);
          }, 0);
        }

        // If we also have gems, set them as secondary (unless we have free entry or ticket upgrade)
        if (gemPrize && !freeEntryPrize && !ticketUpgradePrize) {
          secondaryPrizeType = "Gems";
          secondaryPrizeValue = gemPrize.metadata.amount;
        }
        // Free entry has priority as secondary prize
        else if (freeEntryPrize) {
          secondaryPrizeType = "Free Entry";
          secondaryPrizeValue = 1;
        }
        // Ticket upgrade as secondary if no free entry
        else if (ticketUpgradePrize) {
          secondaryPrizeType = "Level Up";
          secondaryPrizeValue = 1;
        }
      }
      // If no token but we have gems, set gems as primary
      else if (gemPrize) {
        primaryPrizeType = "Gems";
        primaryPrizeValue = gemPrize.metadata.amount;

        // Free entry has priority as secondary prize
        if (freeEntryPrize) {
          secondaryPrizeType = "Free Entry";
          secondaryPrizeValue = 1;
        }
        // Ticket upgrade as secondary if no free entry
        else if (ticketUpgradePrize) {
          secondaryPrizeType = "Level Up";
          secondaryPrizeValue = 1;
        }
      }
      // If only free entry, set it as primary
      else if (freeEntryPrize) {
        secondaryPrizeType = "Free Entry";
        secondaryPrizeValue = 1;
      }
      // If only ticket upgrade, set it as primary
      else if (ticketUpgradePrize) {
        secondaryPrizeType = "Level Up";
        secondaryPrizeValue = 1;
      }
    }

    // Build URL params in a cleaner way
    const baseUrl = `${appConfig.SPLINE_VIEWER}/hXxPtBdLI1zpDAcl`;
    const params = new URLSearchParams();

    // Add required params
    params.append("TierLevel", tierLevel?.toString() || "");
    params.append("PrimaryPrizeType", primaryPrizeType);
    params.append("PrimaryPrizeValue", primaryPrizeValue.toString());

    params.append("SecondaryPrizeType", secondaryPrizeType);
    params.append("SecondaryPrizeValue", secondaryPrizeValue.toString());

    // Add jackpot value
    params.append("JackpotValue", jackpotValue.toString());

    return `${baseUrl}?${params.toString()}`;
  }, [earning]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose?.();
    }, 20000);

    return () => {
      clearTimeout(timer);
    };
  }, [onClose]);

  return (
    <Modal
      visible
      bgOverlaySx={{
        background: "base.color.translucent.inverse.1000",
      }}
    >
      <Modal.Content
        sx={{
          width: "100vw",
          height: "100vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <ButtCon
          icon="Close"
          variant="tertiary"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: "base.spacing.x4",
            top: "base.spacing.x4",
            zIndex: 1,
          }}
        />
        <iframe
          src={url}
          title="Animation representing your weekly reward as it prepares to unlock"
          width="100%"
          height="100%"
          style={{
            border: "none",
            objectFit: "contain",
            colorScheme: "light",
          }}
          loading="eager"
        />
      </Modal.Content>
    </Modal>
  );
};
